import React from 'react';
import Svg, { Circle, Defs, LinearGradient, Path, Stop } from 'react-native-svg';

import { useSvgId } from '../../../../utils/hooks/useSvgId';

/**
 * The glyphs the Invite Friends screen needs that the Figma file does not export - link, copy,
 * share, info - plus the people and back glyphs redrawn from `tab-friends-active.svg` and
 * `back-arrow.svg`. They are drawn in code with an explicit viewBox rather than imported through
 * SVGR, because SVGR drops a viewBox that matches the export size and the icon then clips instead
 * of scaling on a narrow phone (see the note in `assets/friends/index.ts`).
 *
 * Every glyph takes the same gold ramp the friends tab icon paints, or a flat colour.
 */
const GOLD_RAMP: Array<[string, string]> = [
	['0', '#FFFAD4'],
	['0.38', '#FFEC9E'],
	['0.62', '#FFD159'],
	['1', '#D19C59'],
];

type IconProps = { size: number; color?: string };

const GoldRamp = ({ id }: { id: string }) => (
	<Defs>
		<LinearGradient id={id} x1='0' y1='0' x2='0' y2='1'>
			{GOLD_RAMP.map(([offset, color]) => (
				<Stop key={offset} offset={offset} stopColor={color} />
			))}
		</LinearGradient>
	</Defs>
);

/** Two figures - the friends tab glyph, used on the invite card and the "Invited Players" stat. */
export const IconPeople = ({ size }: IconProps) => {
	const fillId = useSvgId('invitePeople');
	const fill = `url(#${fillId})`;

	return (
		<Svg width={size} height={(size * 14) / 16.383} viewBox='0 0 16.383 14'>
			<GoldRamp id={fillId} />
			<Circle cx='5.362' cy='2.979' r='2.979' fill={fill} />
			<Path d='M0 14V10.7234C0 5.95745 10.7234 5.95745 10.7234 10.7234V14H0Z' fill={fill} />
			<Circle cx='12.213' cy='3.277' r='2.383' fill={fill} />
			<Path
				d='M11.3191 14V10.7234C11.3191 8.6383 10.7234 7.74468 10.1276 7.14894C13.4042 5.95745 16.3829 7.74468 16.3829 10.1277V14H11.3191Z'
				fill={fill}
			/>
		</Svg>
	);
};

export const IconLink = ({ size, color = '#FFD159' }: IconProps) => (
	<Svg width={size} height={size} viewBox='0 0 24 24' fill='none'>
		<Path
			d='M10 13a5 5 0 0 0 7.07 0l3-3a5 5 0 0 0-7.07-7.07l-1.5 1.5M14 11a5 5 0 0 0-7.07 0l-3 3a5 5 0 0 0 7.07 7.07l1.5-1.5'
			stroke={color}
			strokeWidth={2.4}
			strokeLinecap='round'
			strokeLinejoin='round'
		/>
	</Svg>
);

export const IconCopy = ({ size, color = '#FFD159' }: IconProps) => (
	<Svg width={size} height={size} viewBox='0 0 24 24' fill='none'>
		<Path
			d='M9 9h10a1 1 0 0 1 1 1v10a1 1 0 0 1-1 1H9a1 1 0 0 1-1-1V10a1 1 0 0 1 1-1Z'
			stroke={color}
			strokeWidth={2.2}
			strokeLinejoin='round'
		/>
		<Path
			d='M16 5V4a1 1 0 0 0-1-1H5a1 1 0 0 0-1 1v10a1 1 0 0 0 1 1h1'
			stroke={color}
			strokeWidth={2.2}
			strokeLinecap='round'
			strokeLinejoin='round'
		/>
	</Svg>
);

export const IconShare = ({ size, color = '#FFD159' }: IconProps) => (
	<Svg width={size} height={size} viewBox='0 0 24 24' fill='none'>
		<Circle cx='18' cy='5' r='3' fill={color} />
		<Circle cx='6' cy='12' r='3' fill={color} />
		<Circle cx='18' cy='19' r='3' fill={color} />
		<Path
			d='M8.6 13.5l6.8 4M15.4 6.5l-6.8 4'
			stroke={color}
			strokeWidth={2.2}
			strokeLinecap='round'
		/>
	</Svg>
);

export const IconInfo = ({ size, color = '#FFD159' }: IconProps) => (
	<Svg width={size} height={size} viewBox='0 0 24 24' fill='none'>
		<Circle cx='12' cy='12' r='10' fill={color} />
		<Path d='M12 11v6' stroke='#2A0A04' strokeWidth={2.6} strokeLinecap='round' />
		<Circle cx='12' cy='7.3' r='1.5' fill='#2A0A04' />
	</Svg>
);

/** `event/back-arrow.svg`, text/accent. */
export const IconBack = ({ size, color = '#FFFF99' }: IconProps) => (
	<Svg width={(size * 12) / 20} height={size} viewBox='0 0 12 20' fill='none'>
		<Path
			d='M10 2L2 10L10 18'
			stroke={color}
			strokeWidth={2.5}
			strokeLinecap='round'
			strokeLinejoin='round'
		/>
	</Svg>
);
